import { Alert, Button, Form, Input, Radio, Spin } from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { customerBankService } from "@/api/customerBank";
import { Breadcrumb, ZhMessageBox } from "@/components/common";

interface BankForm {
    type: number;
    name: string;
    account: string;
    bank?: string;
}
const removeSpaces = (value: string) => value.replace(/\s+/g, "");
/** 按旧页每四位插入空格，仅改变输入展示。 */
const formatCardNumber = (value: string) => value.replace(/(\d{4})(?=\d*$)/g, "$1 ").trim();

/** 银行账户新增/编辑页按账户类型动态校验开户行。 */
export function BankAccountFormPage() {
    const [form] = Form.useForm<BankForm>();
    const type = Form.useWatch("type", form) ?? 1;
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const id = Number(params.get("id") || 0);
    const [loading, setLoading] = useState(Boolean(id));
    const [error, setError] = useState(false);
    const [saving, setSaving] = useState(false);
    const [pendingPayload, setPendingPayload] = useState<BankForm>();
    const pageName = id ? "修改银行账户" : "新增银行账户";
    /** 编辑页只映射旧版允许编辑的四个字段。 */
    const load = async () => {
        if (!id) {
            form.setFieldValue("type", 1);
            return;
        }
        setLoading(true);
        setError(false);
        try {
            const data = await customerBankService.getInfo(id);
            form.setFieldsValue({ type: data.type, account: data.account, name: data.name, bank: data.bank });
        } catch {
            setError(true);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        void load();
    }, [id]);
    /** 旧版编辑态允许历史非数字账号，但新增态必须全数字，二者都限制 8-30 位。 */
    const validateAccount = (_: unknown, value?: string) => {
        if (!value) return Promise.reject(new Error("请输入银行账号"));
        const account = removeSpaces(value);
        if (!/^\d*$/.test(account) && !id) return Promise.reject(new Error("请输入正确格式"));
        if (account.length > 30 || account.length < 8) return Promise.reject(new Error("账号长度必须在8-30个字符之间"));
        return Promise.resolve();
    };
    /** 校验通过后整理卡号，再打开旧版提交确认框。 */
    const requestSave = async () => {
        try {
            const values = await form.validateFields();
            setPendingPayload({
                type: values.type,
                account: removeSpaces(values.account),
                name: values.name,
                bank: values.bank!
            });
        } catch {
            /* 表单组件负责呈现校验错误。 */
        }
    };
    /** 对应旧 confirmWrapper：关闭确认框后提交并返回。 */
    const save = async () => {
        if (!pendingPayload) return;
        const payload = pendingPayload;
        setPendingPayload(undefined);
        setSaving(true);
        try {
            if (id) await customerBankService.update(id, payload);
            else await customerBankService.create(payload);
            navigate(-1);
        } finally {
            setSaving(false);
        }
    };
    /** Vue 的 clearValidate 会在切换账户类型时清除全部字段错误，但保留输入值。 */
    const clearValidation = () => {
        const fieldNames: (keyof BankForm)[] = ["type", "name", "account", "bank"];
        form.setFields(fieldNames.map(name => ({ name, errors: [] })));
    };
    if (loading) return <Spin fullscreen tip="银行账户加载中" />;
    if (error)
        return (
            <Alert
                type="error"
                showIcon
                message="银行账户加载失败"
                action={<Button onClick={() => void load()}>重试</Button>}
            />
        );
    return (
        <section className="bank-account-form-page">
            <div className="section-card bank-account-form-card">
                <Breadcrumb
                    routes={[{ name: "账号管理" }, { name: "银行账户", path: "/me/bankAccount" }, { name: pageName }]}
                />
                <div className="bank-account-form-title">{pageName}</div>
                <Form form={form} initialValues={{ type: 1 }} labelCol={{ flex: "auto" }}>
                    <div className="bank-account-form-grid">
                        <Form.Item name="type" label="账户类型" required>
                            <Radio.Group onChange={clearValidation}>
                                <Radio value={2}>个人账户</Radio>
                                <Radio value={1}>对公账户</Radio>
                            </Radio.Group>
                        </Form.Item>
                        <Form.Item
                            name="name"
                            label={type === 1 ? "开户名称" : "开户人"}
                            required
                            rules={[{ required: true, message: "请输入开户名称" }]}
                        >
                            <Input maxLength={25} placeholder="请输入开户名称" />
                        </Form.Item>
                        <Form.Item
                            name="account"
                            label="银行账号"
                            required
                            normalize={value => formatCardNumber(removeSpaces(String(value ?? "")))}
                            rules={[{ validator: validateAccount }]}
                        >
                            <Input placeholder="请输入银行账号" />
                        </Form.Item>
                        <Form.Item
                            name="bank"
                            label="开户行"
                            required={type === 1}
                            rules={[
                                {
                                    validator: (_, value) =>
                                        type === 1 && !value
                                            ? Promise.reject(new Error("请输入开户行"))
                                            : Promise.resolve()
                                }
                            ]}
                        >
                            <Input maxLength={128} placeholder="请输入开户行" />
                        </Form.Item>
                    </div>
                </Form>
            </div>
            <div className="bank-account-form-footer">
                <Button type="primary" loading={saving} onClick={() => void requestSave()}>
                    提交
                </Button>
                <Button type="primary" onClick={() => navigate(-1)}>
                    返回
                </Button>
            </div>
            <ZhMessageBox
                open={Boolean(pendingPayload)}
                tip="是否提交"
                onCancel={() => setPendingPayload(undefined)}
                onSubmit={save}
            />
        </section>
    );
}
